import { Box, Grid2, Paper, Typography } from '@mui/material';
import React, { Suspense, useEffect, useState } from 'react';
import Breadcrumb from '../../../../components/ui/materialui/breadcrumb';
import { useForm } from 'react-hook-form';
import InputSelect from '../../../../components/inputs/yup/materialui/inputSelect';
import useConsultarRemesas from '../hooks/useConsultarRemesas';
import InputFecha from '../../../../components/inputs/yup/materialui/inputFecha';
import { DestinoRemesas, EstadoProcesoCarga, StatusCode } from '../../../../utils/enums';
import MButton from '../../../../components/inputs/yup/materialui/mButton';
import { Calculate } from '@mui/icons-material';
import { ArgCalcularRemesasSeguroDTO, ArgumentosCalcularRemesasDTO, ArgumentosConsultarStatusDTO, ArgumentosGrabarRemesasDTO, ArgumentosGrabarRemesasSeguros, ProcesoRemesa, RemesaDTO } from '../models/remesas';
import { getCalculoRemesa, getCalculoRemesaSeguros, getStatusRemesa, postRemesa, postRemesaSeguros } from '../services/consultarRemesasServices';
import TextoConLabel from '../../../../components/inputs/genericos/materialui/textoConLabel';
import { descargarReporteRemesas } from '../../../detalleMutuo/services/servicesReportes';
import { useToast } from '../../../../hooks/useToast';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { da } from 'date-fns/locale';

const consultarRemesasFilterSchema = yup.object({
    destino: yup
        .number()
        .required('El destino es obligatorio'),

    fechaRemesa: yup
        .date()
        .required('La fecha de remesa es obligatoria'),

    fechaPrepagoIni: yup
        .date()
        .required('La fecha de prepago inicial es obligatoria'),
    inversionista: yup
        .string()
        .nullable()
        .when('destino', {
            is: (destino: number) => destino === DestinoRemesas.Inversionista,
            then: schema =>
                schema.required('El inversionista es obligatorio'),
            otherwise: s => s.notRequired(),
        }),

    companiaSeguros: yup
        .string()
        .nullable()
        .when('destino', {
            is: (destino: number) => destino === DestinoRemesas.CompaniaSeguros,
            then: schema =>
                schema.required('La compañía de seguros es obligatoria'),
            otherwise: s => s.notRequired(),
        }),

    polizaSeguros: yup
        .string()
        .nullable()
        .when('destino', {
            is: (destino: number) => destino === DestinoRemesas.CompaniaSeguros,
            then: schema =>
                schema.required('La póliza es obligatoria'),
            otherwise: s => s.notRequired(),
        }),
});

interface ConsultarRemesasFilter {
    destino: number;
    fechaRemesa: Date;
    fechaPrepagoIni: Date;
    inversionista?: string | null;
    companiaSeguros?: string | null;
    polizaSeguros?: string | null;
}

const ConsultarRemesas: React.FC = () => {

    const defaultValues: ConsultarRemesasFilter = {
        destino: 1,
        fechaRemesa: new Date(),
        fechaPrepagoIni: new Date(),
        inversionista: '',
        companiaSeguros: '',
        polizaSeguros: ''
    };

    const {
        formState: { errors },
        control,
        handleSubmit,
        watch,
        setValue,
        reset
    } = useForm<ConsultarRemesasFilter>({
        defaultValues: defaultValues,
        mode: "onBlur",
        resolver: yupResolver(consultarRemesasFilterSchema)
    });

    const [remesas, setRemesas] = useState<RemesaDTO>();
    const [procesoRemesas, setProcesoRemesas] = useState<ProcesoRemesa | undefined>();
    const [mensajeProcesoRemesas, setMensajeProcesoRemesas] = useState<string>('');
    const { addToast } = useToast();

    const dataForm = watch();

    const { consultarDestino, consultarInversionista, consultarCompania, consultarPoliza, destinoRemesas, inversionista, compania, poliza } = useConsultarRemesas();
    useEffect(() => {
        consultarDestino();
        consultarInversionista();
        consultarCompania();
        // consultarPoliza(defaultValues.companiaSeguros)
        reset(defaultValues);
        // eslint-disable-next-line
    }, [])

    const handleChangeCompania = async (value: string | null) => {
        if (!value)
            return;

        await consultarPoliza(value);
    }

    const onSubmit = async (data: ConsultarRemesasFilter) => {
        setProcesoRemesas(undefined);

        if (data.destino === DestinoRemesas.CompaniaSeguros) {
            const dataRequest: ArgCalcularRemesasSeguroDTO = {
                nombreCompania: data.companiaSeguros ?? '',
                fechaRemesa: data.fechaRemesa,
                fechaPrepago: data.fechaPrepagoIni,
                codPoliza: data.polizaSeguros ?? ''
            }
            // Lógica para calcular remesas de seguros
            await getCalculoRemesaSeguros(dataRequest);

        } else {
            const dataRequest: ArgumentosCalcularRemesasDTO = {
                rut: data.inversionista ?? '',
                fechaRemesa: data.fechaRemesa,
                fechaPrepago: data.fechaPrepagoIni
            }
            await getCalculoRemesa(dataRequest);
        }

    }

    const handleStatusRemesa = async () => {
        const dataRequest: ArgumentosConsultarStatusDTO = {
            rut: dataForm.inversionista ?? '',
            codigoPoliza: dataForm.polizaSeguros ?? null,
            fechaRemesa: dataForm.fechaRemesa,
            fechaPrepago: dataForm.fechaPrepagoIni,
            nombreCompania: dataForm.companiaSeguros ?? '',
            destino: dataForm.destino
        }
        const resp = await getStatusRemesa(dataRequest);
        if (resp.status === StatusCode.Ok) {
            setProcesoRemesas(resp.data);
        }
    }

    const emitirRemesas = async () => {
        // Lógica para emitir remesas
        descargarReporteRemesas(procesoRemesas?.procesoRemesaId ?? 0, dataForm.destino);
    }

    const grabarRemesas = async () => {
        
        if (Number(dataForm.destino) === DestinoRemesas.CompaniaSeguros) {
            grabarRemesasSeguros();
            return;
        }

        const dataRequest: ArgumentosGrabarRemesasDTO = {
            idProcesoCarga: procesoRemesas?.procesoRemesaId ?? 0,
            rutInv: dataForm.inversionista ?? '',
            fechaRemesa: dataForm.fechaRemesa
        }
        // Lógica para grabar remesas
        const resp = await postRemesa(dataRequest);
        if (resp.status === StatusCode.NoContent) {
            addToast({
                mensaje: 'Remesas grabadas correctamente',
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
            });

            setProcesoRemesas(undefined);
        }
    }

    const grabarRemesasSeguros = async () => {
        const dataRequest: ArgumentosGrabarRemesasSeguros = {
            idProcesoRemesa: procesoRemesas?.procesoRemesaId ?? 0,
        }

        const resp = await postRemesaSeguros(dataRequest);
        if (resp.status === StatusCode.NoContent) {
            addToast({
                mensaje: 'Remesas de seguros grabadas correctamente',
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
            });

            setProcesoRemesas(undefined);
        }
    }

    const breadcrumbItems = [
        { label: 'Inicio', url: '/' },
        { label: 'Remesas', url: `/remesas` },
        { label: 'Consultar remesas', active: true },
    ]

    useEffect(() => {
        setProcesoRemesas(undefined);
    }, [remesas, dataForm.companiaSeguros, dataForm.destino, dataForm.inversionista, dataForm.polizaSeguros, dataForm.fechaRemesa, dataForm.fechaPrepagoIni]);

    useEffect(() => {
        if (procesoRemesas?.estado === EstadoProcesoCarga.ERROR) {
            setMensajeProcesoRemesas(procesoRemesas?.descripcion ?? 'Ha ocurrido un error durante el proceso. Por favor, intente nuevamente.');
        }
        else if (procesoRemesas?.cantidadDatos === -1) {
            setMensajeProcesoRemesas('El cálculo aún está en proceso. Haz clic nuevamente para verificar.');
        }
        else if (procesoRemesas && procesoRemesas?.cantidadDatos >= 0) {
            setMensajeProcesoRemesas(`Cantidad de registros: ${procesoRemesas.cantidadDatos ?? 0}`);
        }
        else {
            setMensajeProcesoRemesas('Ha ocurrido un error. Por favor, intente nuevamente.');
        }

        // eslint-disable-next-line
    }, [procesoRemesas]);

    return (
        <Suspense>
            <Breadcrumb items={breadcrumbItems} />
            <Box sx={{ mt: 2 }}>
                <Paper elevation={3} sx={{ p: 3 }}>
                    <Suspense>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                                <Typography className='card-title' variant="h6" gutterBottom>
                                    Consultar remesas
                                </Typography>

                                <Grid2 container spacing={2} mt={2}>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputSelect
                                            label='Destino'
                                            name={'destino'}
                                            control={control}
                                            options={destinoRemesas}
                                            error={errors.destino}
                                        />
                                    </Grid2>
                                    {
                                        // eslint-disable-next-line
                                        dataForm.destino == DestinoRemesas.Inversionista ?
                                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                                <InputSelect
                                                    label='Inversionista'
                                                    name={'inversionista'}
                                                    control={control}
                                                    options={inversionista}
                                                    error={errors.inversionista}
                                                />
                                            </Grid2>
                                            :
                                            <>
                                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                                    <InputSelect
                                                        label='Compañía seguros'
                                                        name={'companiaSeguros'}
                                                        control={control}
                                                        options={compania}
                                                        onChange={(value) => handleChangeCompania(value)}
                                                        error={errors.companiaSeguros}
                                                    />
                                                </Grid2>
                                                <Grid2 size={{ xs: 12, lg: 3 }}>
                                                    <InputSelect
                                                        label='Póliza seguros'
                                                        name={'polizaSeguros'}
                                                        control={control}
                                                        options={poliza}
                                                        error={errors.polizaSeguros}
                                                    />
                                                </Grid2>
                                            </>
                                    }

                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputFecha
                                            label='Fecha remesa'
                                            name={'fechaRemesa'}
                                            control={control}
                                            fecha={dataForm.fechaRemesa}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, lg: 3 }}>
                                        <InputFecha
                                            label='Fecha prepago ini'
                                            name={'fechaPrepagoIni'}
                                            fecha={dataForm.fechaPrepagoIni}
                                            control={control}
                                        />
                                    </Grid2>

                                    {/* Acciones principales */}
                                    <Grid2 size={{ xs: 12, lg: 12 }} spacing={2} sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", mt: 2 }}>
                                        <Grid2 sx={{ mr: 2 }}>
                                            <MButton
                                                type='submit'
                                                variant='contained'
                                                color='primary'
                                                startIcon={<Calculate />}
                                            >
                                                Calcular remesas
                                            </MButton>
                                        </Grid2>

                                        <Grid2 sx={{ mr: 2 }}>
                                            <MButton
                                                variant='contained'
                                                color='primary'
                                                startIcon={<Calculate />}
                                                onClick={() => handleStatusRemesa()}
                                            >
                                                Consultar status proceso
                                            </MButton>
                                        </Grid2>
                                        {procesoRemesas && (
                                            <Grid2>
                                                <TextoConLabel
                                                    label={
                                                        <>
                                                            Cantidad de registros: {" "}
                                                        </>
                                                    }
                                                    texto={mensajeProcesoRemesas}
                                                />
                                            </Grid2>
                                        )}
                                    </Grid2>
                                </Grid2>
                            </Paper>

                            {/* Sección separada para acciones secundarias */
                                procesoRemesas && procesoRemesas?.cantidadDatos !== -1 &&
                                <Paper elevation={2} sx={{ p: 2, mt: 2 }}>
                                    <Typography className='card-title' variant="h6" gutterBottom>
                                        Acciones sobre remesas
                                    </Typography>
                                    <Grid2 container spacing={2} sx={{ alignItems: "center" }}>
                                        <Grid2>
                                            <MButton
                                                variant='contained'
                                                color='primary'
                                                startIcon={<Calculate />}
                                                onClick={() => emitirRemesas()}
                                            >
                                                Emitir remesas
                                            </MButton>
                                        </Grid2>
                                        <Grid2>
                                            <MButton
                                                variant='contained'
                                                color='primary'
                                                startIcon={<Calculate />}
                                                onClick={() => grabarRemesas()}
                                            >
                                                Grabar remesas
                                            </MButton>
                                        </Grid2>
                                    </Grid2>
                                </Paper>
                            }
                        </form>
                    </Suspense>
                </Paper>
            </Box>
        </Suspense>
    );
};

export default ConsultarRemesas;